'use client'
import React from 'react'
import { Form, Button, Space, Input, Row, Col, Typography, notification } from 'antd'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useMutation } from '@apollo/client'
import { UPDATE_WORKLOG_VERSIONED } from '../gql/verteOfficeGQL'
import { worklogBadge } from '../../WorklogStatusBadge/WorklogStatusBadge'
import { requiredTrimmedRule } from '../shared/validation/validationUtils'

const { Text } = Typography

interface CompleteWorklogObservationFormProps {
  worklogId: string
  rowVersion: number
  startTime: string | Date
  endTime: string | Date
  onCancel: () => void
  onClose: () => void
}

export const CompleteWorklogObservationForm: React.FC<CompleteWorklogObservationFormProps> = ({
  worklogId,
  rowVersion,
  startTime,
  endTime,
  onCancel,
  onClose,
}) => {
  const [updateWorklogVersioned, { loading }] = useMutation(UPDATE_WORKLOG_VERSIONED, {
    fetchPolicy: 'no-cache',
  })
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const formattedStart = dayjs(startTime).format('DD/MM/YYYY HH:mm')
  const formattedEnd = dayjs(endTime).format('DD/MM/YYYY HH:mm')

  const handleSubmit = async (values: any) => {
    try {
      const result = await updateWorklogVersioned({
        variables: {
          id: worklogId,
          expectedRowVersion: rowVersion,
          data: {
            status: 'COMPLETE',
            observation: values.observation.trim(),
          },
        },
      })

      const response = result.data?.updateWorklogVersioned
      if (response?.ok === false) {
        if (response.errorCode === 'VALIDATION_FAILED' && response.fieldErrors?.length > 0) {
          notification.error({ message: response.fieldErrors[0].message })
        } else {
          notification.error({ message: t('error_ending_worklog') })
        }
        return
      }

      notification.success({ message: t('worklog_ended_successfully') })
      worklogBadge.refresh()
      onClose()
    } catch (error: any) {
      notification.error({
        message: t('error_ending_worklog'),
        description: error.message,
      })
    } finally {
      form.resetFields()
    }
  }

  return (
    <Form form={form} layout='vertical' onFinish={handleSubmit}>
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <div
            style={{
              padding: '12px',
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '6px',
              marginBottom: '16px',
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Actividad cerrada automáticamente
            </Text>
            <Text type='secondary' style={{ fontSize: '0.85rem' }}>
              Inicio: {formattedStart}
            </Text>
            <br />
            <Text type='secondary' style={{ fontSize: '0.85rem' }}>
              Fin: {formattedEnd}
            </Text>
            <br />
            <Text type='secondary' style={{ fontSize: '0.85rem' }}>
              Total: 2h
            </Text>
          </div>
        </Col>

        <Col span={24}>
          <Form.Item
            name='observation'
            label={t('observations')}
            rules={[requiredTrimmedRule(t('field_required'))]}
            style={{ marginBottom: 8 }}
          >
            <Input.TextArea
              size='small'
              rows={4}
              style={{ width: '100%' }}
              placeholder={t('observations')}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 0,
            }}
          >
            <Space>
              <Button onClick={onCancel} size='small' shape='round' danger>
                {t('cancel')}
              </Button>
              <Button
                type='primary'
                htmlType='submit'
                shape='round'
                size='small'
                loading={loading}
              >
                {t('finish')}
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default CompleteWorklogObservationForm
